import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowCounterClockwise, Check, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RevertRequests({ onBack }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReverts();
  }, []);

  const fetchReverts = async () => {
    try {
      const response = await axios.get(`${API}/admin/revert-requests`);
      setRequests(response.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API}/admin/revert-requests/${id}/action`, { action });
      toast.success(`Revert request ${action}ed successfully`);
      fetchReverts();
    } catch (err) {
      toast.error('Failed to process revert request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      {/* Header Bar - Green matching reference screenshot */}
      <div className="bg-[#23d366] text-white px-4 py-3 shadow-md flex items-center gap-3 flex-shrink-0">
        {onBack && (
          <button onClick={onBack} className="p-1 hover:bg-black/10 rounded-full transition-colors">
            <ArrowLeft size={22} weight="bold" />
          </button>
        )}
        <div>
          <h1 className="text-base font-bold leading-tight">Revert Requests</h1>
          <p className="text-[11px] text-white/90">Admin Control Center</p>
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 p-4 md:p-8 max-w-2xl w-full mx-auto space-y-3">
        <h2 className="text-sm font-bold text-slate-800 mb-2">Pending Withdrawal Revert Requests</h2>

        {loading ? (
          <div className="p-12 text-center text-slate-500 bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2" />
            <p className="text-xs font-semibold">Loading revert requests...</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-slate-400 bg-white rounded-xl border border-slate-200 shadow-sm">
            <ArrowCounterClockwise size={32} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-medium">No pending revert requests</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {requests.map((req) => (
              <div
                key={req.id}
                className="bg-white rounded-xl p-3.5 border border-slate-200 shadow-sm flex items-center justify-between gap-4 hover:border-slate-300 transition-colors"
                data-testid={`revert-request-${req.id}`}
              >
                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-xs text-slate-800 uppercase tracking-tight">
                    Client: {req.client_name || req.phone || 'Customer'}
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Field: <span className="font-bold text-indigo-600">{req.field_name || 'Withdrawal'}</span>
                  </p>
                  {req.old_value && (
                    <p className="text-xs text-slate-400">Previous: <span className="line-through">{req.old_value}</span></p>
                  )}
                  {req.new_value && (
                    <p className="text-xs text-emerald-600 font-semibold">New: {req.new_value}</p>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">
                    Requested by: {req.requested_by_name || 'Handler'} ({formatDate(req.created_at)})
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleAction(req.id, 'approve')}
                    className="bg-[#00a884] hover:bg-[#008f72] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-colors flex items-center gap-1"
                    data-testid={`approve-revert-btn-${req.id}`}
                  >
                    <Check size={14} weight="bold" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleAction(req.id, 'reject')}
                    className="border border-red-500 text-red-500 hover:bg-red-50 text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                    data-testid={`reject-revert-btn-${req.id}`}
                  >
                    <X size={14} weight="bold" />
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
